/**
 * Quick capture filled from Dialpad's recap of the call it was opened from (spec
 * `docs/superpowers/specs/2026-11-01-after-call-draft-design.md`). Pure: what to fill, given what
 * the rep has already touched, and the one line that says where things stand. A field the rep
 * typed in is never overwritten; a recap that lands late fills only what is still untouched.
 */
import { type CallsDraftOutput } from '@lsc/contract';
import { DRAFT_RESOLUTION } from '@lsc/domain';

/** How long the form keeps asking after it opened. Dialpad's worst recap so far: 76 s after hangup. */
export const DRAFT_ASK_MS = 90_000;
export const DRAFT_POLL_MS = 3_000;

export interface DraftForm {
  subject: string;
  notes: string;
  resolveTicked: boolean;
  resolution: string;
}

export interface DraftTouched {
  subject: boolean;
  notes: boolean;
  resolve: boolean;
}

/** The fields to set from the draft; only empty, untouched ones. */
export function fillFromDraft(form: DraftForm, touched: DraftTouched, d: CallsDraftOutput): Partial<DraftForm> {
  const out: Partial<DraftForm> = {};
  if (!touched.subject && form.subject.trim() === '' && d.subject) out.subject = d.subject;
  if (!touched.notes && form.notes.trim() === '' && d.notes) out.notes = d.notes;
  if (!touched.resolve && d.resolvedOnCall && !form.resolveTicked) {
    out.resolveTicked = true;
    if (form.resolution.trim() === '') out.resolution = DRAFT_RESOLUTION;
  }
  return out;
}

export type DraftStatus = 'waiting' | 'filled' | 'none';

/** Waiting while the API says so (or has not answered) and the form has asked for under 90 s. */
export function draftStatus(d: CallsDraftOutput | undefined, failed: boolean, askedForMs: number): DraftStatus {
  if (d && (d.subject !== null || d.notes !== null)) return 'filled';
  if (failed) return 'none';
  if ((!d || d.waiting) && askedForMs < DRAFT_ASK_MS) return 'waiting';
  return 'none';
}

export const DRAFT_WORDS: Record<DraftStatus, string> = {
  waiting: "Waiting on Dialpad's recap",
  filled: "Filled from Dialpad's recap. Check it before you log.",
  none: 'Dialpad sent no recap for this call.',
};

/** Keep asking every 3 s while waiting; stop once there is something or the 90 s are up. */
export function nextDraftPoll(d: CallsDraftOutput | undefined, askedForMs: number): number | false {
  return draftStatus(d, false, askedForMs) === 'waiting' ? DRAFT_POLL_MS : false;
}
